import { safeJson } from "@/lib/http";
import { supabase } from "@/lib/supabase";

export const BUCKET = "dataroom";
export const MAX_BYTES = 50 * 1024 * 1024;
export const ACCEPT = ".pdf,.xlsx,.xls,.csv,.txt,.docx,.png,.jpg,.jpeg";

export const TYPES_DOCUMENT = [
    { value: "pv_jugement", label: "PV et Jugements" },
    { value: "document_administratif", label: "Documents Administratifs" },
    { value: "fec", label: "FEC (écritures comptables)" },
    { value: "liasse_fiscale", label: "Liasse fiscale" },
    { value: "balance_clients", label: "Balance âgée clients" },
    { value: "balance_fournisseurs", label: "Balance âgée fournisseurs" },
    { value: "tableau_previsionnel_client", label: "Tableau prévisionnel client (Excel/CSV/PDF)" },
    { value: "rib", label: "RIB" },
    { value: "releve_bancaire", label: "Relevé bancaire" },
    { value: "tableau_amortissements", label: "Tableau des dotations aux amortissements" },
    { value: "tableau_stocks", label: "Évaluation des stocks" },
    { value: "valorisation_immobilisations", label: "Valorisation des immobilisations" },
    { value: "identite_gerant", label: "Pièce d'identité du gérant" },
    { value: "kbis", label: "K-Bis (< 3 mois)" },
    { value: "etat_privileges", label: "État des privilèges et nantissements" },
    { value: "statuts", label: "Statuts de la société" },
    { value: "pv_ag", label: "PV d'Assemblée Générale" },
    { value: "jugement", label: "Jugement (procédure collective)" },
    { value: "attestations_fiscales_sociales", label: "Attestations fiscales et sociales" },
    { value: "tableau_amortissement_pret", label: "Tableau d'amortissement du prêt" },
    { value: "devis_fournisseurs", label: "Devis fournisseurs (x3)" },
    { value: "devis_cabinet_conseil", label: "Devis cabinet de conseil" },
    { value: "facture", label: "Facture (demande de virement)" },
    { value: "devis", label: "Devis (demande de virement)" },
    { value: "bon_commande", label: "Bon de commande" },
    { value: "justificatif_virement", label: "Justificatif de virement" },
    { value: "logo", label: "Logo" },
    { value: "photos", label: "Photos" },
    { value: "entite_garantie_caution", label: "Entité garantie et caution" },
    { value: "fiches_paie", label: "Fiches de paie (6 derniers mois)" },
    { value: "autre", label: "Autres" },
];

const CURRENT_YEAR = new Date().getFullYear();
export const YEARS = Array.from({ length: 16 }, (_, i) => CURRENT_YEAR - i);

export function detectYear(filename) {
    const m = String(filename || "").match(/20\d{2}/g);
    if (!m) return "";
    const ok = m.map(Number).filter((y) => y >= CURRENT_YEAR - 15 && y <= CURRENT_YEAR);
    return ok.length ? String(Math.max(...ok)) : "";
}

const TYPE_ORDER = Object.fromEntries(TYPES_DOCUMENT.map((t, i) => [t.value, i]));
export function sortDocuments(docs) {
    const year = (d) => Number((String(d.periode || "").match(/20\d{2}/) || [0])[0]);
    return [...docs].sort((a, b) => (TYPE_ORDER[a.type_document] ?? 99) - (TYPE_ORDER[b.type_document] ?? 99) || year(b) - year(a) || new Date(b.created_at) - new Date(a.created_at));
}

export function labelDocument(v) {
    return TYPES_DOCUMENT.find((t) => t.value === v)?.label ?? v;
}

export function formatBytes(n) {
    if (!n) return "—";
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} Ko`;
    return `${(n / 1024 / 1024).toFixed(1)} Mo`;
}

const safeName = (name) => name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(-150);

// SUPER-LOT 84 — Espace Livrables : 4 phases d'une procédure collective (préfixes de chemin Supabase Storage).
export const LIVRABLE_PHASES = [
    { key: "1-premier-rdv-aj", label: "1 - Premier RDV AJ" },
    { key: "2-audience-ouverture", label: "2 - Audience d'Ouverture" },
    { key: "3-periode-obs", label: "3 - Période d'Obs." },
    { key: "4-arrete-plan", label: "4 - Arrêté du Plan" },
];

export function lastModifiedLabel(ts) {
    if (!ts) return "—";
    return new Date(ts).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const BASE_COLS = "id, nom_fichier, type_document, type_personnalise, periode, uploaded_by_email, url_stockage, created_at, inclure_analyse, est_holding";
const LIVRABLE_COLS = "est_livrable, livrable_phase, requires_signature, signe, signed_source_id";

export async function fetchDocuments(dossierId) {
    const q = (cols) => supabase.from("documents_dataroom").select(cols).eq("dossier_id", dossierId).order("created_at", { ascending: false });
    // Tolérant à l'absence de la migration : repli progressif sur les colonnes disponibles.
    let { data, error } = await q(`${BASE_COLS}, remarques, ${LIVRABLE_COLS}`);
    if (error) ({ data, error } = await q(`${BASE_COLS}, remarques`));
    if (error) ({ data, error } = await q(BASE_COLS));
    if (error) throw error;
    // Anti-OOM navigateur : jamais le texte extrait, seulement un drapeau (ids ayant un contenu non vide).
    const { data: flags } = await supabase.from("documents_dataroom").select("id").eq("dossier_id", dossierId).not("contenu_extrait", "is", null).neq("contenu_extrait", "");
    const ok = new Set((flags ?? []).map((f) => f.id));
    return (data ?? []).map((d) => ({ ...d, contenu_extrait: ok.has(d.id) ? "ok" : null }));
}

// SUPER-LOT 84 — Upload Admin d'un livrable dans le bucket dataroom sous {dossierId}/livrables/{phase}/
export async function uploadLivrable(dossierId, file, phase, requiresSignature = false) {
    if (file.size > MAX_BYTES) throw new Error("Fichier trop volumineux (50 Mo maximum).");
    const path = `${dossierId}/livrables/${phase}/${crypto.randomUUID()}-${safeName(file.name)}`;
    const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type || undefined, upsert: false });
    if (upErr) throw upErr;
    const { data, error } = await supabase.from("documents_dataroom")
        .insert({ dossier_id: dossierId, nom_fichier: file.name, type_document: "livrable", url_stockage: path, est_livrable: true, livrable_phase: phase, requires_signature: requiresSignature })
        .select(`${BASE_COLS}, ${LIVRABLE_COLS}`).single();
    if (error) { await supabase.storage.from(BUCKET).remove([path]); throw error; }
    return data;
}

// SUPER-LOT 84 — Récupère uniquement les livrables (filtre serveur, évite de charger toute la Dataroom).
export async function fetchLivrables(dossierId) {
    const { data, error } = await supabase.from("documents_dataroom")
        .select(`${BASE_COLS}, ${LIVRABLE_COLS}`).eq("dossier_id", dossierId).eq("est_livrable", true)
        .order("created_at", { ascending: false });
    if (error) { const all = await fetchDocuments(dossierId); return all.filter((d) => d.est_livrable); }
    return data ?? [];
}

export async function uploadDocument(dossierId, file, typeDocument, periode = "", typePersonnalise = "", estHolding = false) {
    if (file.size > MAX_BYTES) throw new Error("Fichier trop volumineux (50 Mo maximum).");
    const path = `${dossierId}/${crypto.randomUUID()}-${safeName(file.name)}`;

    const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type || undefined, upsert: false });
    if (upErr) throw upErr;

    const { data, error } = await supabase
        .from("documents_dataroom")
        .insert({ dossier_id: dossierId, nom_fichier: file.name, type_document: typeDocument, type_personnalise: typePersonnalise.trim() || null, periode: periode.trim() || null, url_stockage: path, est_holding: estHolding })
        .select("id, nom_fichier, type_document, type_personnalise, periode, uploaded_by_email, url_stockage, created_at, est_holding")
        .single();
    if (error) {
        await supabase.storage.from(BUCKET).remove([path]);
        throw error;
    }
    return data;
}

async function apiCall(path, method) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, { method, headers: { Authorization: `Bearer ${token}` } });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(j.detail || `Erreur ${r.status}`);
    return j;
}

export const extraireDocumentAsync = (docId) => apiCall(`/api/documents/${docId}/extraire-async`, "POST");
export const extractionStatut = (docId) => apiCall(`/api/documents/${docId}/extraction-statut`, "GET");
export const extraireDateJugement = (docId) => apiCall(`/api/documents/${docId}/extract-date-jugement`, "POST");
export const dateJugementStatut = (docId) => apiCall(`/api/documents/${docId}/date-jugement-statut`, "GET");

export async function toggleAnalyseDocument(docId) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/toggle-analyse`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}` },
    });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(j.detail || `Erreur ${r.status}`);
    return j;
}

export const extraireDocument = extraireDocumentAsync;

export async function getDownloadUrl(path) {
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 300, { download: true });
    if (error) throw error;
    return data.signedUrl;
}

export async function deleteDocument(doc) {
    const { error: sErr } = await supabase.storage.from(BUCKET).remove([doc.url_stockage]);
    if (sErr) throw sErr;
    const { error } = await supabase.from("documents_dataroom").delete().eq("id", doc.id);
    if (error) throw error;
}

// SUPER-LOT 103 — Actions de masse & renommage inline.
export async function renameDocument(id, nom) {
    const clean = String(nom || "").trim();
    if (!clean) throw new Error("Le nom ne peut pas être vide.");
    const { error } = await supabase.from("documents_dataroom").update({ nom_fichier: clean }).eq("id", id);
    if (error) throw error;
    return clean;
}

export async function moveDocuments(ids, typeDocument) {
    if (!ids.length) return;
    const { error } = await supabase.from("documents_dataroom").update({ type_document: typeDocument }).in("id", ids);
    if (error) throw error;
}

export async function deleteDocuments(docs) {
    const paths = docs.map((d) => d.url_stockage).filter(Boolean);
    if (paths.length) await supabase.storage.from(BUCKET).remove(paths);
    const { error } = await supabase.from("documents_dataroom").delete().in("id", docs.map((d) => d.id));
    if (error) throw error;
}

export async function setRemarquesDocument(docId, remarques) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/remarques`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ remarques }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.detail || `Erreur ${r.status}`);
    return j;
}
